import React, { useState, useCallback, useMemo } from 'react'
import { Box, FormLabel, MenuItem, TextField, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import useGTM from '@elgorditosalsero/react-gtm-hook'
import useSiteMetadata from '../../hooks/useSiteMetadata'
import useContactMutation from '../../hooks/useContactMutation'
import {
  ContactFormFields,
  useContactFormValidation,
} from '../../hooks/useContactFormValidation'
import { BrandNames } from '../../types/brand'
import { Endpoints } from '../../types/constants'
import TimeChip from '../ContentfulEntries/TimeChip'
import useMultiStepModal from '../Modals/useMultiStepModal'
import CaptchaProvider from '../Providers/Captcha'
import LoaderButton from '../../components/Buttons/LoaderButton'
import { useCommonStyles } from '../../utils/styles'

interface Props {
  inSection?: boolean
}

type FormFieldsAndErrorFieldsType =
  | Record<keyof ContactFormFields, string | null>
  | Record<`${keyof ContactFormFields}_error_message`, string | null>

export const ContactForm: React.FC<Props> = ({ inSection }) => {
  const { classes: commonClasses } = useCommonStyles()
  const { t } = useTranslation()
  const { brandName } = useSiteMetadata()
  const { classes, cx } = useStyles()
  const { sendDataToGTM } = useGTM()
  const { handleClose } = useMultiStepModal()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const validateSchema = useContactFormValidation()
  const [data, setData] = useState<ContactFormFields>({
    contact_name: '',
    contact_email: '',
    contact_genre: null,
    contact_comments: '',
  })
  const [errorState, setErrorState] = useState<
    Record<`${keyof ContactFormFields}_error_message`, string | null>
  >({
    contact_name_error_message: null,
    contact_email_error_message: null,
    contact_genre_error_message: null,
    contact_comments_error_message: null,
  })

  const { mutate, status, isLoading, reset } = useContactMutation()

  const resetForm = () => {
    setData({
      contact_name: '',
      contact_email: '',
      contact_genre: null,
      contact_comments: '',
    })
  }

  const hasAnyError = (object: FormFieldsAndErrorFieldsType) => {
    return Object.values(object).some((error) => error !== null)
  }
  const isAnyError = useMemo(() => hasAnyError(errorState), [errorState])

  const submitForm = useCallback<React.FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()
      reset()

      const validatedObject = validateSchema(data)

      const hasErrors = hasAnyError(validatedObject)

      if (hasErrors) {
        setErrorState({
          contact_name_error_message: validatedObject['contact_name'],
          contact_email_error_message: validatedObject['contact_email'],
          contact_comments_error_message: validatedObject['contact_comments'],
          contact_genre_error_message: validatedObject['contact_genre'],
        })
        return
      }

      const recaptchaToken = await executeRecaptcha?.('contact')

      const submitMutation = (recaptchaToken: string) => {
        const payload =
          brandName.toLowerCase() === BrandNames.zipJob
            ? {
                ...data,
                recaptcha_token: recaptchaToken,
              }
            : {
                ...data,
                grecaptcha3_tok: recaptchaToken,
                site_domain: brandName.toLowerCase(),
              }

        const endpoint =
          brandName.toLowerCase() === BrandNames.zipJob
            ? Endpoints.ContactFormZJ
            : Endpoints.ContactFormCommon
        mutate({ endpoint, payload }, { onSuccess: resetForm })
      }

      if (recaptchaToken) {
        submitMutation(recaptchaToken)
        sendDataToGTM({
          event: 'CustomEvents',
          eventAction: 'Contact Form Submitted',
          eventCategory: 'Contact',
          eventLabel: 'Message Sent',
        })
      }

      handleClose()
    },
    [
      data,
      handleClose,
      sendDataToGTM,
      executeRecaptcha,
      validateSchema,
      reset,
      brandName,
      mutate,
    ]
  )

  const handleChange = useCallback(
    (
        fieldName: keyof ContactFormFields
      ): React.ChangeEventHandler<HTMLInputElement> =>
      (event) => {
        setData((d) => ({
          ...d,
          [fieldName]: event.target.value,
        }))
        const field = `${fieldName}_error_message`
        setErrorState((prev) => ({ ...prev, [field]: null }))
      },
    []
  )

  return (
    <form className={classes.form} onSubmit={submitForm} noValidate>
      <FormLabel>
        <Typography className={classes.title} variant="h3">
          {t('contactForm.formLabel')}
        </Typography>
      </FormLabel>
      <Box className={classes.selectBox}>
        <TextField
          className={classes.selectRoot}
          select
          fullWidth
          variant="outlined"
          label={t('contactForm.inquiry')}
          onChange={handleChange('contact_genre')}
          required
          error={!!errorState.contact_genre_error_message}
          helperText={errorState.contact_genre_error_message}
          value={data.contact_genre}
        >
          {Object.entries(
            t<string>('contactForm.contactGenres', { returnObjects: true })
          ).map(([value, label]) => (
            <MenuItem className={classes.menuItem} key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TextField
        className={classes.textField}
        label={t('contactForm.name')}
        variant="outlined"
        onChange={handleChange('contact_name')}
        required
        error={!!errorState.contact_name_error_message}
        helperText={errorState.contact_name_error_message}
        value={data.contact_name}
      />
      <TextField
        className={classes.textField}
        label={t('contactForm.email')}
        variant="outlined"
        onChange={handleChange('contact_email')}
        type="email"
        required
        error={!!errorState.contact_email_error_message}
        helperText={errorState.contact_email_error_message}
        value={data.contact_email}
      />
      <TextField
        className={classes.textField}
        InputProps={{
          classes: {
            input:
              brandName === BrandNames.zipJob
                ? classes.inputLargeZJ
                : cx(
                    classes.inputLarge,
                    classes.overflow,
                    commonClasses.hideScrollbar
                  ),
            root: classes.textFieldLarge,
          },
        }}
        label={t('contactForm.helpYou')}
        variant="outlined"
        multiline
        //min and max rows props are required for the text area to work correctly
        minRows={1}
        maxRows={10}
        onChange={handleChange('contact_comments')}
        required
        error={!!errorState.contact_comments_error_message}
        helperText={errorState.contact_comments_error_message}
        value={data.contact_comments}
      />
      <Box
        className={cx(
          classes.submitBox,
          brandName.toLowerCase() === BrandNames.zipJob && classes.submitBoxZJ
        )}
      >
        <Box className={classes.submitInfoBox}>
          <Typography
            className={
              brandName.toLowerCase() === BrandNames.zipJob
                ? classes.required
                : classes.requiredTR
            }
          >
            {t('contactForm.required')}
          </Typography>
          {!inSection && <TimeChip chip={{ time: t('contactForm.time') }} />}
        </Box>
        <LoaderButton
          className={classes.button}
          color="primary"
          variant="contained"
          type="submit"
          loading={isLoading}
          disabled={isAnyError || isLoading}
        >
          {t(inSection ? 'contactForm.submitInSection' : 'contactForm.submit')}
        </LoaderButton>
      </Box>
      {status !== 'idle' && (
        <Box mt={2}>
          {status === 'success' && (
            <Typography className={classes.successText}>
              {t('commonFormMessages.submitSuccess')}
            </Typography>
          )}
          {status === 'error' && (
            <Typography align="center" color="error">
              {t('commonFormMessages.submitFail')}
            </Typography>
          )}
        </Box>
      )}
    </form>
  )
}

const useStyles = makeStyles()((theme) => ({
  button: {
    minWidth: 'fit-content',
    [theme.breakpoints.down('md')]: {
      marginTop: '1.25em',
      width: '100%',
    },
  },
  form: {
    textAlign: 'left',
  },
  inputLargeZJ: {
    overflow: 'scroll !important',
    maxHeight: '11.5em',
  },
  inputLarge: {
    overflow: 'hidden',
    maxHeight: '11.5em',
  },
  menuItem: {
    '&:hover': {
      backgroundColor: theme.colors.gray[100],
    },
  },
  required: {
    color: theme.palette.text.secondary,
  },
  requiredTR: {
    color: theme.palette.secondary.dark,
  },
  selectBox: {
    marginTop: '1.25em',
    position: 'relative',
    '& .MuiInputLabel-root': {
      [theme.breakpoints.down('sm')]: {
        width: '78%!important',
      },
    },
  },
  selectRoot: {
    width: '100%',
    '& > div': {
      [theme.breakpoints.down('md')]: {
        lineHeight: '3',
      },
    },
  },
  submitBox: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1.25em',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  submitBoxZJ: {
    [theme.breakpoints.up('md')]: {
      gap: '1.5rem',
    },
  },
  submitInfoBox: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '68%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  textField: {
    marginTop: '1.25em',
    width: '100%',
    '& .MuiFormLabel-root': {
      [theme.breakpoints.down('sm')]: {
        width: '90%!important',
      },
    },
  },
  textFieldLarge: {
    alignItems: 'flex-start',
    height: '12.5em',
  },
  title: {
    color: theme.palette.text.primary,
  },
  successText: {
    color: theme.palette.success.main,
  },
  overflow: {
    overflow: 'auto',
  },
}))

const ContactFormWithCaptcha: React.FC<Props> = ({ inSection }) => (
  <CaptchaProvider>
    <ContactForm inSection={inSection} />
  </CaptchaProvider>
)

export default ContactFormWithCaptcha
